import React, { useEffect, useState } from 'react';
import { BadgeCheckIcon, CheckIcon, CogIcon, FileTextIcon, LibraryBigIcon, MailIcon, ShieldAlertIcon, StarIcon, XIcon, ZapIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { AccordionItem } from '../components/AccordionItem';
import { OfferCard } from '../components/OfferCard';
import { useScreenInit } from '../useScreenInit';
const productImage = 'https://cdn.magicpatterns.com/uploads/3qACt3yjFfzyf2kgRptLN5/ChatGPT_Image_Jul_10,_2026,_05_43_40_PM.png';
const checkoutUrl = 'https://usa.ebookpoint.shop/cart/54923957567860:1';
const modules = [['⚾ Volume 1 — Pitching Mechanics: Health and Performance (55 Pages)', ['Why health and performance are not separate topics', 'Functional anatomy of the throwing motion (full kinetic chain, feet to hand)', 'Windup phase: errors that reduce velocity and add unnecessary load', 'Arm cocking phase: the point of highest UCL stress — and highest power loss if flawed', 'Acceleration phase: how hip-trunk-arm sequencing determines real velocity', 'Release phase: timing and its impact on control and joint load', 'Follow-through (deceleration) phase: why a cut-short follow-through both hurts and slows the pitch', 'Video self-assessment: 5 checkpoints any pitcher can do alone', 'Most common mechanical errors by pitch type (fastball, breaking ball, changeup)']], ['🛡️ Volume 2 — Injury Prevention by Breakdown Point (55 Pages)', ['Injury is accumulated load, not a single event', 'The UCL: why it gets injured, early warning signs most pitchers ignore', 'Rotator cuff and forearm strengthening protocol', 'Shoulder: impingement from accumulated fatigue over a long season', 'Scapular stability protocol', 'Elbow: telling normal training soreness from a real warning sign', 'Core and hip: the overlooked link (weak here means more load on the arm)', 'Anti-rotational core strength protocol applied to the throwing motion', 'Quick-reference table: symptom → likely cause → immediate action', 'When to actually stop (and why pitching through pain costs more time than resting on time)']], ['🏋️ Volume 3 — Season Strength and Conditioning Program (55 Pages)', ['Periodization for the amateur pitcher: pre-season, in-season, post-season', 'Pre-season phase (8 weeks): building a strength and mobility base', 'In-season phase: maintenance without adding fatigue that affects pitching', 'Post-season phase: active rest and corrective work', 'Arm strengthening routine (resistance band, light weight, weekly progression)', 'Leg and hip work: the power source many pitchers neglect', 'Pitching-specific warm-up protocol (12-15 min, full routine)', 'Cooldown and recovery after games or training', 'How to measure real progress (velocity, not just how it feels)']], ["🧠 Volume 4 — The Pitcher's Psychology: The Invisible Half of the Game (45 Pages)", ["Why the best arm in the world fails if the mind doesn't follow", 'Managing pressure in key moments (bases loaded, 3-2 count, playoffs)', 'Pre-pitch routines: building a ritual that cuts anxiety', 'Focus and mental reset between pitches (the "next pitch" mindset)', 'Processing mistakes: how professional pitchers process a home run in 10 seconds and move on', 'Technical confidence vs. mental confidence: why they are not the same thing', 'Performance anxiety in young pitchers: recognizing it and working through it', "The mental return after an injury: fear of re-injury and how it's addressed (ties into Volume 5)", 'Applied visualization: a simple, evidence-based protocol, not an esoteric one', 'Communicating with a coach or parent: asking for help without it feeling like weakness']], ['📈 Volume 5 — Workload Management and Return to the Mound (45 Pages)', ["Why pitch count alone isn't enough (accumulated load vs. single-game load)", 'Pitch count and rest guidelines by age (adapted from USA Baseball references)', 'Fatigue signals that precede injury (mechanics breaking down under tiredness)', 'Post-pitching recovery protocol (48-72 hours)', 'Return progression after an arm injury (phase by phase)', 'Returning after a long layoff (offseason, injury, inactivity)', 'Green/yellow/red progression checklist to safely advance phases', 'Guide for parents/coaches: supporting without overprotecting or pressuring', "Final checklist: arm, mechanics, and mind all need to be ready — one alone isn't enough"]]] as const;
const bonuses = [{
  title: '🎯 Bonus 1 — Risk & Performance Calculator (Interactive Excel)',
  previewImage: 'https://cdn.magicpatterns.com/uploads/q29VQNeEfzMGszXLgv8gGM/Bono1.png',
  previewAlt: 'Risk and Performance Calculator preview',
  lines: ['the user enters age, weekly pitch volume, current discomfort level, and main goal (health vs. velocity). The tool calculates a Risk Score, points to the priority protocol in Volume 2, and adds a performance note tied to Volume 1.']
}, {
  title: '🗓️ Bonus 2 — Your First 4 Weeks (PDF, 3 Pages)',
  previewImage: 'https://cdn.magicpatterns.com/uploads/rLXthTMiuSmULwwyYsLJMs/Bono_2.png',
  previewAlt: 'Your First 4 Weeks roadmap preview',
  lines: ['a day-by-day roadmap for the first month, showing which sections of each volume to use and when, based on the Bonus 1 result. It sequences all the other bonuses in the right order.']
}, {
  title: '📋 Bonus 3 — Season Pitch Count & Rest Planner (Interactive Excel, 20-Week Log)',
  previewImage: 'https://cdn.magicpatterns.com/uploads/sAwihNafhatq34eGV5x8C7/Bono_3.png',
  previewAlt: 'Season Pitch Count and Rest Planner preview',
  lines: ['a weekly log of pitches thrown by age group, with an automatic OK / OVER LIMIT flag (color-coded) based on USA Baseball reference guidelines, plus the minimum rest days needed.']
}, {
  title: '🧘 Bonus 4 — Pre-Game Mental Reset Protocol (PDF, 3 Pages)',
  previewImage: 'https://cdn.magicpatterns.com/uploads/abv9PAmQh6xwMB9STTjXGu/Bono_4.png',
  previewAlt: 'Pre-Game Mental Reset Protocol preview',
  lines: ['a 10-minute, 4-step routine to run before taking the mound (physical down-regulation, mental rehearsal, anchor, final activation), a direct application of Volume 4.']
}, {
  title: '📊 Bonus 5 — 12-Week Progression Tracker (Interactive Excel with Auto-Updating Chart)',
  previewImage: 'https://cdn.magicpatterns.com/uploads/hPJ8vKBvqriW8UMDW9AEQV/Bono_5.png',
  previewAlt: '12-Week Progression Tracker preview',
  lines: ['an editable companion to Volume 3. The user logs weekly strength score, estimated velocity, and arm discomfort; a line chart updates automatically to show the trend across all three.']
}] as const;
const reviews = [['Megan R., Austin, TX', 'My 13-year-old finally understands why his arm needs a plan, not just more throwing.'], ['David K., Tampa, FL', 'The mechanics checklists gave us a simple way to talk through every bullpen.'], ['Rachel P., Phoenix, AZ', 'Clear, practical, and perfect for a parent who wants to support without pushing.'], ['Chris B., Columbus, OH', 'My son, 15, uses the pre-game routine before every start. He feels calmer and prepared.'], ['Lauren S., Denver, CO', 'The workload planner helped us make smarter decisions during tournament season.']] as const;
const notifications = ['Mason from Dallas, TX', 'Olivia from Charlotte, NC', 'Ethan from San Diego, CA', 'Noah from Nashville, TN'];
const comparisonRows = [['Waits for pain to become a problem', 'Recognizes warning signs and adjusts early'], ['Trains without a clear structure', 'Follows clear phases throughout the season'], ['Relies on arm strength alone', 'Trains mechanics, body, and mind together'], ['Counts pitches without context', 'Manages workload and recovery with purpose']] as const;
export function SalesLanding() {
  useScreenInit();
  const [notice, setNotice] = useState(0);
  useEffect(() => {
    const interval = window.setInterval(() => setNotice(value => (value + 1) % notifications.length), 6500);
    return () => window.clearInterval(interval);
  }, []);
  return <main className="min-h-screen bg-[#f6f0e4] text-[#10243d]" data-id="element-62">
      <div className="ballpark-bg" aria-hidden="true" data-id="element-63" />

      <header className="relative overflow-hidden bg-[#10243d] px-4 pb-10 pt-6 text-[#fff8ea]" data-id="element-64">
        <div className="mx-auto max-w-md text-center" data-id="element-65">
          <p className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-[#f3a23a]" data-id="element-66">
            Baseball Pitcher Without Limits
          </p>
          <h1 className="mt-4 font-heading text-4xl font-bold uppercase leading-[0.96] sm:text-5xl" data-id="element-67">
            Protect His Arm.
            <br data-id="element-68" />
            Improve His Game.
            <br data-id="element-69" />
            <span className="text-[#f3a23a]" data-id="element-70">One Complete System.</span>
          </h1>

          <a href="#oferta" className="mt-6 block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f3a23a]" data-id="element-71">
            <img src={productImage} alt="Baseball Pitcher Without Limits complete system for pitchers" className="mx-auto aspect-[4/5] w-full max-w-[248px] rounded-xl border-4 border-[#f3a23a] object-cover shadow-2xl" data-id="element-72" />
          </a>

          <p className="mx-auto mt-5 max-w-[390px] text-sm font-semibold leading-5 tracking-[-0.01em]" data-id="element-73">
            A complete mechanics, injury-prevention, and mental-game system —
            built for amateur and youth pitchers who want to stay on the mound..
          </p>

          <div className="mx-auto mt-6 grid max-w-[380px] grid-cols-3 overflow-hidden rounded-xl border border-[#36506d] bg-[#17314f] shadow-lg" data-id="element-74">
            <div className="flex min-h-[78px] flex-col items-center justify-center border-r border-[#36506d] px-2 text-center" data-id="element-75">
              <FileTextIcon className="h-5 w-5 text-[#f3a23a]" aria-hidden="true" data-id="element-76" />
              <span className="mt-2 text-[11px] font-bold uppercase tracking-wide" data-id="element-77">
                100% PDF
              </span>
              <span className="mt-0.5 text-[9px] uppercase tracking-[0.12em] text-[#afc0d0]" data-id="element-78">
                Digital format
              </span>
            </div>
            <div className="flex min-h-[78px] flex-col items-center justify-center border-r border-[#36506d] px-2 text-center" data-id="element-79">
              <ZapIcon className="h-5 w-5 text-[#f3a23a]" aria-hidden="true" data-id="element-80" />
              <span className="mt-2 text-[11px] font-bold uppercase tracking-wide" data-id="element-81">
                Instant Delivery
              </span>
              <span className="mt-0.5 text-[9px] uppercase tracking-[0.12em] text-[#afc0d0]" data-id="element-82">
                Access today
              </span>
            </div>
            <div className="flex min-h-[78px] flex-col items-center justify-center px-2 text-center" data-id="element-83">
              <LibraryBigIcon className="h-5 w-5 text-[#f3a23a]" aria-hidden="true" data-id="element-84" />
              <span className="mt-2 text-[11px] font-bold uppercase tracking-wide" data-id="element-85">
                5 + 5
              </span>
              <span className="mt-0.5 text-[9px] uppercase tracking-[0.12em] text-[#afc0d0]" data-id="element-86">
                Modules + Bonuses
              </span>
            </div>
          </div>

          <a href="#oferta" className="mt-6 block rounded-lg bg-[#e56822] px-5 py-4 font-heading text-lg font-bold uppercase tracking-wide text-white shadow-lg transition hover:bg-[#f17b35] focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-87">
            Protect His Arm Today
          </a>
        </div>
      </header>

      <section className="relative px-4 py-12" aria-labelledby="risk-title" data-id="element-88">
        <div className="mx-auto max-w-md" data-id="element-89">
          <p className="font-heading text-sm font-bold uppercase tracking-[0.18em] text-[#c65216]" data-id="element-90">
            The reality of the mound
          </p>
          <h2 id="risk-title" className="mt-2 font-heading text-3xl font-bold uppercase leading-tight" data-id="element-91">
            We know the hidden danger of being a pitcher.
          </h2>
          <ul className="mt-6 space-y-3" data-id="element-92">
            {['Elbow or shoulder pain that gets normalized until it gets worse.', 'Lost velocity caused by inefficient mechanics.', 'Built-up fatigue without a clear recovery plan.', 'Mechanical breakdowns under pressure or fatigue.', 'Anxiety in tough counts and after a bad pitch.'].map(risk => <li key={risk} className="flex gap-3 rounded-lg border-l-4 border-[#e56822] bg-white/90 p-4 text-sm font-medium shadow-sm" data-id="element-93">
                <ShieldAlertIcon className="h-5 w-5 shrink-0 text-[#e56822]" data-id="element-94" />
                {risk}
              </li>)}
          </ul>
        </div>
      </section>

      <section className="relative bg-[#fffaf0] px-4 py-12" aria-labelledby="system-title" data-id="element-95">
        <div className="mx-auto max-w-md" data-id="element-96">
          <h2 id="system-title" className="font-heading text-3xl font-bold uppercase" data-id="element-97">
            That&apos;s why we built this system
          </h2>
          <p className="mt-3 text-sm leading-6 text-[#526170]" data-id="element-98">
            A complete plan to understand movement, protect the arm, and compete
            with confidence.
          </p>
          <div className="mt-6 space-y-3" data-id="element-99">
            {modules.map(([title, lines], index) => <AccordionItem key={title} title={title} lines={[...lines]} index={index} data-id="element-100" />)}
          </div>
        </div>
      </section>

      <section className="relative px-4 py-12" aria-labelledby="reviews-title" data-id="element-101">
        <div className="mx-auto max-w-md" data-id="element-102">
          <h2 id="reviews-title" className="font-heading text-3xl font-bold uppercase" data-id="element-103">
            From families who want to throw better and safer
          </h2>
          <div className="mt-6 space-y-3" data-id="element-104">
            {reviews.map(([name, review]) => <figure key={name} className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-[#e7d9c4]" data-id="element-105">
                <div className="flex gap-1 text-[#e56822]" aria-label="5 out of 5 stars" data-id="element-106">
                  {Array.from({
                length: 5
              }).map((_, index) => <StarIcon key={index} className="h-4 w-4 fill-current" data-id="element-107" />)}
                </div>
                <blockquote className="mt-3 text-sm leading-6 text-[#344658]" data-id="element-108">
                  “{review}”
                </blockquote>
                <figcaption className="mt-3 text-xs font-bold uppercase tracking-wide text-[#10243d]" data-id="element-109">
                  {name}
                </figcaption>
              </figure>)}
          </div>
        </div>
      </section>

      <section className="relative bg-[#10243d] px-4 py-12 text-[#fff8ea]" aria-labelledby="bonus-title" data-id="element-110">
        <div className="mx-auto max-w-md" data-id="element-111">
          <p className="font-heading text-sm font-bold uppercase tracking-[0.18em] text-[#f3a23a]" data-id="element-112">
            Free with your purchase today
          </p>
          <h2 id="bonus-title" className="mt-2 font-heading text-3xl font-bold uppercase leading-tight" data-id="element-113">
            We don&apos;t want to leave any gaps uncovered, so today with your
            purchase:
          </h2>
          <div className="mt-6 space-y-4" data-id="element-114">
            {bonuses.map((bonus, index) => <AccordionItem key={bonus.title} title={bonus.title} lines={[...bonus.lines]} index={index + 10} previewImage={bonus.previewImage} previewAlt={bonus.previewAlt} data-id="element-115" />)}
          </div>
        </div>
      </section>

      <section className="relative bg-[#fffaf0] px-4 py-12" aria-labelledby="compare-title" data-id="element-116">
        <div className="mx-auto max-w-md" data-id="element-117">
          <div className="overflow-hidden rounded-xl border border-[#d8c8ad] bg-white shadow-sm" data-id="element-118">
            <h2 id="compare-title" className="border-b border-[#d8c8ad] bg-[#10243d] px-4 py-4 text-center font-heading text-2xl font-bold uppercase leading-tight text-[#fff8ea]" data-id="element-119">
              The difference a complete system makes
            </h2>
            <table className="w-full table-fixed border-collapse text-left text-xs" data-id="element-120">
              <caption className="sr-only" data-id="element-121">
                Without Baseball Pitcher Without Limits compared with Baseball
                Pitcher Without Limits
              </caption>
              <thead data-id="element-122">
                <tr data-id="element-123">
                  <th className="w-1/2 border-r border-[#e7a9a4] bg-[#f7dfdc] p-3 font-heading text-sm uppercase leading-4 text-[#812d25]" data-id="element-124">
                    Without
                    <span className="mt-1 block text-[10px] leading-3 normal-case text-[#9d4239]" data-id="element-125">
                      Baseball Pitcher Without Limits
                    </span>
                  </th>
                  <th className="w-1/2 bg-[#dff0df] p-3 font-heading text-sm uppercase leading-4 text-[#1f6337]" data-id="element-126">
                    With
                    <span className="mt-1 block text-[10px] leading-3 normal-case text-[#317847]" data-id="element-127">
                      Baseball Pitcher Without Limits
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-128">
                {comparisonRows.map(([without, withSystem], index) => <tr key={without} data-id="element-129">
                    <td className={`border-r border-[#efc2bd] p-3 align-top text-[#743a33] ${index % 2 === 0 ? 'bg-[#fff1ef]' : 'bg-[#fbe5e2]'}`} data-id="element-130">
                      <span className="flex gap-1.5" data-id="element-131">
                        <XIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#b13b31]" data-id="element-132" />
                        {without}
                      </span>
                    </td>
                    <td className={`p-3 align-top font-semibold text-[#245336] ${index % 2 === 0 ? 'bg-[#eff9ee]' : 'bg-[#e5f4e4]'}`} data-id="element-133">
                      <span className="flex gap-1.5" data-id="element-134">
                        <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#267444]" data-id="element-135" />
                        {withSystem}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <OfferCard data-id="element-136" />

      <section className="relative bg-[#fffaf0] px-4 py-12" aria-labelledby="faq-title" data-id="element-137">
        <div className="mx-auto max-w-md" data-id="element-138">
          <h2 id="faq-title" className="font-heading text-3xl font-bold uppercase" data-id="element-139">
            Questions before you start
          </h2>
          <div className="mt-6 space-y-3" data-id="element-140">
            <AccordionItem index={10} title="Is this for young pitchers?" lines={['Yes. It is designed for amateur and youth pitchers, with workload guidelines organized by age.']} data-id="element-141" />
            <AccordionItem index={11} title="Do we need special equipment?" lines={['No. The system uses accessible resources: a resistance band, light weight, and a clear routine.']} data-id="element-142" />
            <AccordionItem index={12} title="Does this replace a doctor or coach?" lines={['No. This is an educational tool to support more informed decisions. For pain or injury, consult a healthcare professional.']} data-id="element-143" />
            <AccordionItem index={13} title="How do I receive the materials?" lines={['Your digital materials are delivered immediately after you complete your purchase.']} data-id="element-144" />
          </div>

          <div className="mt-10 rounded-xl border border-[#d8c8ad] bg-white p-5 text-center" data-id="element-145">
            <CogIcon className="mx-auto h-8 w-8 text-[#e56822]" aria-hidden="true" data-id="element-146" />
            <p className="mt-2 font-heading text-sm font-bold uppercase" data-id="element-147">
              Support
            </p>
            <a className="mt-1 inline-flex items-center gap-2 text-sm font-semibold text-[#c65216] underline" href="mailto:EbookDreamShop@gmail.com" data-id="element-148">
              <MailIcon className="h-4 w-4" data-id="element-149" />
              EbookDreamShop@gmail.com
            </a>
          </div>
        </div>
      </section>

      <AnimatePresence mode="wait" data-id="element-150">
        <motion.aside key={notifications[notice]} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 10
      }} transition={{
        duration: 0.24,
        ease: 'easeOut'
      }} className="fixed bottom-4 left-4 z-20 max-w-[260px] rounded-lg border border-[#d8c8ad] bg-[#fffdf7] p-3 shadow-xl" role="status" aria-live="polite" data-id="element-151">
          <div className="flex gap-2" data-id="element-152">
            <BadgeCheckIcon className="h-5 w-5 shrink-0 text-[#e56822]" data-id="element-153" />
            <p className="text-xs leading-5" data-id="element-154">
              <strong data-id="element-155">{notifications[notice]}</strong>
              <br data-id="element-156" />
              just purchased this digital book.
            </p>
          </div>
        </motion.aside>
      </AnimatePresence>

      <a href={checkoutUrl} className="sr-only" data-id="element-157">
        Purchase Baseball Pitcher Without Limits
      </a>
    </main>;
}